import React, { useMemo, useRef, useState, useEffect, useContext } from "react";
import { toast } from "react-hot-toast";
import { Filter, RefreshCcw, XCircle, RotateCcw } from "lucide-react";
import { GlobalContext } from "../../../Context/GlobalContext";

import useTransactions from "../../Services/PaymentServices/useTransactions";
const PAGE_SIZE = 200;

export default function ReconRefundsReport() {
  const {getRefundReportAdmin}=useTransactions();

  const { api } = useContext(GlobalContext);

  // Default: current date only
  const today = () => new Date().toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [search, setSearch] = useState("");

  // Optional: you can keep these filters if you want (not required by API)
  const [bankCodeFilter, setBankCodeFilter] = useState("all"); // all | 00 | ...
  const [bankStatusFilter, setBankStatusFilter] = useState("all"); // all | SUCCESS | ...

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // ✅ only auto-load once on tab open
  const didAutoLoadRef = useRef(false);

  useEffect(() => {
    if (didAutoLoadRef.current) return;
    didAutoLoadRef.current = true;
    fetchRefundsReport({ silent: true }); // load on tab open with current date
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* -------------------- API Fetch -------------------- */
const fetchRefundsReport = async ({
  silent,
} = {}) => {
  if (!fromDate || !toDate) {
    toast.error(
      "Please select From & To date"
    );
    return;
  }

  if (fromDate > toDate) {
    toast.error(
      "From date cannot be after To date"
    );
    return;
  }

  setLoading(true);

  try {
    const result =
      await getRefundReportAdmin({
        fromDate,
        toDate,
      });

    if (result.success) {
      setRows(result.data);
      setPage(1);

      if (!silent)
        toast.success(result.message);
    } else {
      toast.error(result.message);
      setRows([]);
      setPage(1);
    }
  } catch (e) {
    toast.error(
      "Failed to fetch refund report"
    );
    setRows([]);
    setPage(1);
  } finally {
    setLoading(false);
  }
};


  /* -------------------- Helpers -------------------- */
  const parseDateSafe = (v) => {
    // API: "2026-01-13T12:45:11"
    const d = new Date(v || 0);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `₹ ${n.toFixed(2)}` : "₹ 0.00";
  };

  /* -------------------- Derived -------------------- */
  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => parseDateSafe(b.in_date) - parseDateSafe(a.in_date));
  }, [rows]);

  const bankCodes = useMemo(() => {
    const set = new Set();
    for (const r of sortedRows) if (r?.bank_responseCode != null) set.add(String(r.bank_responseCode));
    return ["all", ...Array.from(set)];
  }, [sortedRows]);

  const bankStatuses = useMemo(() => {
    const set = new Set();
    for (const r of sortedRows) if (r?.bank_msg) set.add(String(r.bank_msg));
    return ["all", ...Array.from(set)];
  }, [sortedRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const s = (v) => (v ?? "").toString().toLowerCase();

    return sortedRows.filter((r) => {
      const matchesSearch = !q
        ? true
        : [
            r.id,
            r.userid,
            r.orderId,
            r.trans_Id,
            r.bank_responseCode,
            r.bank_msg,
            r.bank_status,
            r.transactionRefid,
            r.instructionIdentification,
            r.amount,
            r.endToEndIdentification,
            r.in_date,
            r.refunded_by,
          ]
            .map(s)
            .some((v) => v.includes(q));

      const matchesCode =
        bankCodeFilter === "all" ? true : s(r.bank_responseCode) === bankCodeFilter.toLowerCase();

      const matchesStatus =
        bankStatusFilter === "all" ? true : s(r.bank_msg) === bankStatusFilter.toLowerCase();

      return matchesSearch && matchesCode && matchesStatus;
    });
  }, [sortedRows, search, bankCodeFilter, bankStatusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageSafe = (p) => Math.min(totalPages, Math.max(1, p));
  const paginated = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  const clearFilters = () => {
    setSearch("");
    setBankCodeFilter("all");
    setBankStatusFilter("all");
    setPage(1);
  };

  const badgeByCode = (code) => {
    const c = String(code ?? "").toUpperCase();
    const ok = c === "00";
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
          ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"
        }`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {c || "—"}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Refunds</h2>
          <p className="text-sm text-gray-500">
            Loads on tab open (current date), and on Apply.
          </p>
        </div>

        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
          onClick={() => fetchRefundsReport({ silent: false })}
          disabled={loading}
          title="Refresh"
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} /> Filters
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">From</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={fromDate}
              max={today()}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">To</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={toDate}
              max={today()}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <div className="flex h-10 w-80 items-center gap-2 rounded-xl border px-3">
              <input
                className="h-full w-full text-sm outline-none"
                placeholder="Order / VA / E2E / Msg / Ref / Amount / User"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
              {search && (
                <button
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                  onClick={() => setSearch("")}
                >
                  <XCircle size={16} />
                </button>
              )}
            </div>
          </label>

          {/* Optional filters (useful in real data) */}
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Bank Code</div>
            <select
              className="h-10 w-40 rounded-xl border bg-white px-3 text-sm outline-none"
              value={bankCodeFilter}
              onChange={(e) => {
                setBankCodeFilter(e.target.value);
                setPage(1);
              }}
            >
              {bankCodes.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "All" : c}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Bank Msg</div>
            <select
              className="h-10 w-56 rounded-xl border bg-white px-3 text-sm outline-none"
              value={bankStatusFilter}
              onChange={(e) => {
                setBankStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {bankStatuses.map((m) => (
                <option key={m} value={m}>
                  {m === "all" ? "All" : m}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto flex gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>

            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#EF4444] hover:bg-[#DC2626] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
              onClick={() => fetchRefundsReport({ silent: false })}
              disabled={loading}
            >
              {loading ? "Applying…" : "Apply"}
            </button>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          Records: <b className="text-gray-700">{filteredRows.length}</b>
          {rows.length > 0 && (
            <>
              {" "}
              (Fetched: <b className="text-gray-700">{rows.length}</b>)
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#EF4444] hover:bg-[#DC2626] text-white">
              <tr>
                {[
                  "S/N",
                  "ID",
                  "User ID",
                  "Order ID",
                  "Trans ID (VA)",
                  "Bank Code",
                  "Bank Msg",
                  "Bank Status",
                  "Txn Ref ID",
                  "Instruction ID",
                  "Amount",
                  "EndToEnd ID",
                  "In Date",
                  "Refunded By",
                ].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={14} className="px-4 py-10 text-center text-gray-500">
                    No refunds found for selected range.
                  </td>
                </tr>
              )}

              {rows.length > 0 && paginated.length === 0 && !loading && (
                <tr>
                  <td colSpan={14} className="px-4 py-10 text-center text-gray-500">
                    No records found for current filters.
                  </td>
                </tr>
              )}

              {paginated.map((r, idx) => {
                const bankCode = r?.bank_responseCode;
                const ok = String(bankCode ?? "").toUpperCase() === "00";

                return (
                  <tr
                    key={`${r.id}-${idx}`}
                    className={`border-t ${ok ? "bg-emerald-50/30" : "bg-yellow-100/40"}`}
                    title={ok ? "Refund success" : "Refund not OK / check bank code"}
                  >
                    <td className="px-4 py-2">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td className="px-4 py-2 font-semibold">{r.id ?? "—"}</td>
                    <td className="px-4 py-2">{r.userid ?? "—"}</td>
                    <td className="px-4 py-2 font-semibold">{r.orderId ?? "—"}</td>
                    <td className="px-4 py-2">{r.trans_Id ?? "—"}</td>
                    <td className="px-4 py-2">{badgeByCode(bankCode)}</td>
                    <td className="px-4 py-2">{r.bank_msg ?? "—"}</td>
                    <td className="px-4 py-2">{r.bank_status ?? "—"}</td>
                    <td className="px-4 py-2">{r.transactionRefid ?? "—"}</td>
                    <td className="px-4 py-2">{r.instructionIdentification ?? "—"}</td>
                    <td className="px-4 py-2">{money(r.amount)}</td>
                    <td className="px-4 py-2">{r.endToEndIdentification ?? "—"}</td>
                    <td className="px-4 py-2">
                      {r.in_date ? new Date(r.in_date).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-2">{r.refunded_by ?? "—"}</td>
                  </tr>
                );
              })}

              {loading && (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm disabled:opacity-60"
              onClick={() => setPage((p) => pageSafe(p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </span>
            <button
              className="h-9 rounded-xl border px-3 text-sm disabled:opacity-60"
              onClick={() => setPage((p) => pageSafe(p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
